import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    ArrowLeft, Phone, Envelope, Power, Crown, ShieldStar, ClipboardText,
    ClockClockwise, Warning, ChartBar, Globe, Handshake, Briefcase, ArrowRight,
    User, IdentificationCard, Clock, Circle, Factory, CheckCircle, ArrowsCounterClockwise,
} from "@phosphor-icons/react";
import { CommentRequiredDialog } from "@/components/admin/CommentRequiredDialog";
import { AvatarUploader } from "@/components/admin/coe/AvatarUploader";
import { SessionsPanel } from "@/components/admin/coe/SessionsPanel";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";

/* ============================================================================
   AdminCoeMemberProfile — `/admin/coe/people/:id`
   ----------------------------------------------------------------------------
   6 tabs:
       Overview    — edit name/email/phone/role + Danger Zone (deactivate)
       Permissions — resolved role + permission grid (editable)
       Workload    — open leads currently owned + recent assignments
       Sessions    — login_events history + online-now pill
       Activity    — actions THIS member has performed (audit_log actor_id = id)
       Audit       — actions taken on THIS member (entity_type=coe_member, entity_id=id)
   ========================================================================== */

const TABS = [
    { id: "overview",    label: "Overview",    Icon: ClipboardText },
    { id: "permissions", label: "Permissions", Icon: ShieldStar },
    { id: "workload",    label: "Workload",    Icon: ChartBar },
    // iter101.28 — "Planned cases" surfaces on planner members only.
    // Provides per-planner throughput (total, this-month, this-week,
    // active-open, refinement-rate) and every case ever assigned.
    // Critical for external-planner billing + internal performance
    // review; before this tab, admins had zero visibility into a
    // planner's case throughput.
    { id: "cases",       label: "Planned cases", Icon: Factory },
    { id: "sessions",    label: "Sessions",    Icon: Clock },
    { id: "activity",    label: "Activity",    Icon: ClockClockwise },
    { id: "audit",       label: "Audit",       Icon: ClockClockwise },
];

const initials = (n) => (n || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const fmtDT = (s) => s
    ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

export default function AdminCoeMemberProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tab, setTab] = useState("overview");
    const [member, setMember] = useState(null);
    const [catalog, setCatalog] = useState(null);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get(`/admin/coe/people/${id}`),
            api.get("/admin/coe/roles/catalog"),
            api.get("/admin/coe/roles"),
        ]).then(([m, c, r]) => {
            setMember(m.data);
            setCatalog(c.data);
            setRoles(r.data || []);
        }).catch(() => setMember(null)).finally(() => setLoading(false));
    };
    useEffect(load, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const doToggle = async (comment) => {
        try {
            await api.put(`/admin/coe/people/${id}/toggle`, { comment });
            toast.success(member.is_active ? "Deactivated" : "Activated");
            setConfirmOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
            throw e;
        }
    };

    if (loading && !member) return <div className="p-6 text-muted-foreground text-sm">Loading user…</div>;
    if (!member) {
        return (
            <div className="bg-card border border-border rounded-2xl p-10 text-center">
                <Warning size={32} className="mx-auto mb-2 text-muted-foreground" />
                <h2 className="font-display text-xl font-bold mb-1">User not found.</h2>
                <Button onClick={() => navigate("/admin/coe")} variant="outline" className="rounded-full mt-3">Back to COE</Button>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="admin-coe-profile">
            <Link to="/admin/coe" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                data-testid="coe-profile-back">
                <ArrowLeft size={14} /> Back to COE Command Center
            </Link>

            {/* Header */}
            <div className="bg-card border border-border rounded-2xl p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-4 min-w-0 flex-1">
                        <AvatarUploader
                            src={member.avatar_url || member.image || ""}
                            name={member.name}
                            size={64}
                            uploadPath={`/admin/coe/people/${member.id}/photo`}
                            clearPath={`/admin/coe/people/${member.id}/photo`}
                            canEdit
                            onUpdated={() => load()}
                            isAdminBadge={member.role === "admin"}
                            testid="coe-profile-avatar"
                        />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="coe-profile-name">
                                    {member.name || "Unnamed"}
                                </h1>
                                {member.role === "admin" && (
                                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-amber-200 bg-amber-100 text-amber-800 font-bold inline-flex items-center gap-1">
                                        <Crown size={10} weight="fill" /> Admin
                                    </span>
                                )}
                                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-primary/20 bg-primary/10 text-primary font-bold inline-flex items-center gap-1">
                                    <ShieldStar size={10} weight="fill" /> {member.role_label}
                                </span>
                                {member.custom_role_name && (
                                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border-2 border-dashed border-primary/40 text-primary font-bold">
                                        Custom role
                                    </span>
                                )}
                                {member.has_permission_overrides && (
                                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-amber-200 bg-amber-100 text-amber-800 font-bold">
                                        Custom permissions
                                    </span>
                                )}
                                {member.is_active === false && (
                                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-rose-200 bg-rose-100 text-rose-700 font-bold">
                                        Deactivated
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                                {member.phone && <span className="flex items-center gap-1"><Phone size={11} /> {member.phone}</span>}
                                {member.email && <span className="flex items-center gap-1"><Envelope size={11} /> {member.email}</span>}
                                {member.last_login_at && <span className="flex items-center gap-1"><ClockClockwise size={11} /> Last login {fmtDT(member.last_login_at)}</span>}
                            </div>
                        </div>
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmOpen(true)}
                        className={member.is_active === false
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600"
                            : "text-rose-600 border-rose-200 hover:bg-rose-50"}
                        data-testid="coe-profile-toggle"
                    >
                        <Power size={12} className="mr-1.5" />
                        {member.is_active === false ? "Activate" : "Deactivate"}
                    </Button>
                </div>

                {/* Quick stats strip */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-border">
                    <Stat label="Open leads" value={member.open_leads_count} sub={`${member.open_web}w · ${member.open_b2b}b · ${member.open_application}a`} />
                    <Stat label="Closed (lifetime)" value={member.closed_leads_count} sub={`${member.closed_web}w · ${member.closed_b2b}b · ${member.closed_application}a`} />
                    <Stat label="Joined" value={member.created_at ? new Date(member.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"} />
                    <Stat label="Status" value={member.is_active === false ? "Deactivated" : "Active"} tone={member.is_active === false ? "rose" : "emerald"} />
                </div>
            </div>

            {/* Tabs */}
            <AdminSegmentedTabs
                tabs={TABS
                    // iter101.28 — Hide "Planned cases" for non-planner
                    // members. The tab is meaningless for QA / production
                    // / sales / counsellors — filter out at render time
                    // so the tab shelf stays clean.
                    .filter((t) => t.id !== "cases" || (member.staff_type === "planner"))
                    .map((t) => ({
                        id: t.id,
                        label: t.label,
                        Icon: t.Icon,
                        count:
                            t.id === "workload" ? (member.open_leads_count ?? 0) : null,
                    }))}
                active={tab}
                onChange={setTab}
                testid="coe-profile-tabs"
                tabTestidBase="coe-profile-tab"
                showZero
            />

            {tab === "overview" && (
                <OverviewTab member={member} roles={roles} onSaved={load} />
            )}
            {tab === "permissions" && (
                <PermissionsTab member={member} catalog={catalog} onChanged={load} />
            )}
            {tab === "workload" && (
                <WorkloadTab memberId={id} />
            )}
            {tab === "cases" && (
                <PlannedCasesTab memberId={id} />
            )}
            {tab === "sessions" && (
                <SessionsPanel memberId={id} />
            )}
            {tab === "activity" && (
                <ActivityTab memberId={id} />
            )}
            {tab === "audit" && (
                <AuditTab memberId={id} />
            )}

            <CommentRequiredDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                title={member.is_active === false ? `Activate ${member.name}?` : `Deactivate ${member.name}?`}
                description={member.is_active === false
                    ? "They will regain access immediately and can resume work. Please note the reason for reactivation."
                    : "They will lose access immediately. All their work history and lead ownership are preserved. Please note the reason for deactivation."}
                ctaLabel={member.is_active === false ? "Activate user" : "Deactivate user"}
                ctaVariant={member.is_active === false ? "default" : "destructive"}
                onConfirm={doToggle}
            />
        </div>
    );
}

function Stat({ label, value, sub, tone }) {
    const tlc = tone === "emerald" ? "text-emerald-700"
        : tone === "rose" ? "text-rose-700" : "";
    return (
        <div>
            <div className={`text-2xl font-display font-bold tabular-nums leading-none ${tlc}`}>{value}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mt-1">{label}</div>
            {sub && <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{sub}</div>}
        </div>
    );
}

/* ============================ Overview tab ============================ */
function OverviewTab({ member, roles, onSaved }) {
    const [form, setForm] = useState({
        name: member.name || "",
        phone: member.phone || "",
        email: member.email || "",
        staff_type: member.staff_type || "counsellor",
        custom_role_id: member.custom_role_id || "",
    });
    const [saving, setSaving] = useState(false);

    const builtIn = roles.filter((r) => r.is_built_in);
    const custom = roles.filter((r) => !r.is_built_in);

    const isDirty = useMemo(() => (
        form.name !== (member.name || "") ||
        form.phone !== (member.phone || "") ||
        form.email !== (member.email || "") ||
        form.staff_type !== (member.staff_type || "counsellor") ||
        (form.custom_role_id || "") !== (member.custom_role_id || "")
    ), [form, member]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/coe/people/${member.id}`, {
                ...form,
                custom_role_id: form.custom_role_id || null,
            });
            toast.success("Saved");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setSaving(false);
    };

    return (
        <div className="grid lg:grid-cols-3 gap-5" data-testid="coe-profile-overview">
            <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5 space-y-3">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Edit profile</div>
                <Fld label="Name">
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="coe-profile-name-input" />
                </Fld>
                <Fld label="Phone">
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="coe-profile-phone-input" />
                </Fld>
                <Fld label="Email">
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="coe-profile-email-input" />
                </Fld>
                <Fld label="Built-in role">
                    <select value={form.staff_type} onChange={(e) => setForm({ ...form, staff_type: e.target.value })}
                        className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                        data-testid="coe-profile-staff-type">
                        {builtIn.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                    </select>
                </Fld>
                {custom.length > 0 && (
                    <Fld label="Custom role override (takes precedence over built-in)">
                        <select value={form.custom_role_id} onChange={(e) => setForm({ ...form, custom_role_id: e.target.value })}
                            className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                            data-testid="coe-profile-custom-role">
                            <option value="">— None (use built-in) —</option>
                            {custom.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </Fld>
                )}
                <div className="flex justify-end pt-2">
                    <Button onClick={save} disabled={saving || !isDirty} data-testid="coe-profile-save">
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </div>
            </div>
            <div className="space-y-5">
                <div className="bg-card border border-border rounded-2xl p-5">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-3">Quick facts</div>
                    <dl className="space-y-2 text-sm">
                        <Row label="ID"><span className="font-mono text-xs break-all">{member.id}</span></Row>
                        <Row label="Role">{member.role_label}</Row>
                        <Row label="Joined">{member.created_at ? new Date(member.created_at).toLocaleDateString("en-IN") : "—"}</Row>
                        <Row label="Invited by">{member.invited_by_id ? <span className="font-mono text-xs">{member.invited_by_id.slice(0, 8)}…</span> : "—"}</Row>
                    </dl>
                </div>
                {/* iter101.39 — For counsellors, surface the "Homepage
                    settings" card so admins can tune the admin-only levers
                    (rating, ratings_count, sort_order, availability
                    override, feature pin) right here without hopping to
                    Landing CMS. The read-only preview beneath makes it
                    obvious that PES-owned fields (credentials, designation,
                    languages, public bio) are edited by the counsellor
                    themselves from My Profile. */}
                {member.staff_type === "counsellor" && (
                    <HomepageSettingsCard member={member} onSaved={onSaved} />
                )}
                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-5">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-rose-700 mb-2">Danger zone</div>
                    <p className="text-xs text-rose-800 mb-3">
                        Deactivating prevents this member from logging in. Their work history, audit trail, and lead ownership remain intact.
                    </p>
                </div>
            </div>
        </div>
    );
}


/* iter101.39 — Homepage settings card. Renders only for counsellors and
   only inside the Admin Team & Access profile page. Lets admins tune the
   4 admin-only levers (rating, ratings_count, sort_order, availability
   override) + Feature button; every PES-owned field appears read-only
   with an "edited by {name} in My Profile" hint so ownership is crystal
   clear. */
function HomepageSettingsCard({ member, onSaved }) {
    const prof = member.counsellor_profile || {};
    const [form, setForm] = useState({
        rating: prof.rating || 0,
        ratings_count: prof.ratings_count || 0,
        sort_order: prof.sort_order || 0,
        is_available_now: !!prof.is_available_now,
    });
    const [saving, setSaving] = useState(false);
    const [featuring, setFeaturing] = useState(false);
    const firstName = (member.name || "").split(/\s+/)[0] || "This counsellor";

    const save = async () => {
        setSaving(true);
        try {
            await Promise.all([
                api.put(`/admin/landing/counsellors/${member.id}/profile`, {
                    rating: Number(form.rating) || 0,
                    ratings_count: Number(form.ratings_count) || 0,
                    sort_order: Number(form.sort_order) || 0,
                }),
                api.patch(`/admin/landing/counsellors/${member.id}/availability`, {
                    is_available_now: form.is_available_now,
                }),
            ]);
            toast.success("Homepage settings saved.");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally { setSaving(false); }
    };

    const feature = async () => {
        setFeaturing(true);
        try {
            await api.post(`/admin/landing/counsellors/${member.id}/feature`);
            toast.success(`${firstName} pinned as featured counsellor.`);
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't feature.");
        } finally { setFeaturing(false); }
    };

    const languages = (member.languages || []).join(", ");

    return (
        <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="homepage-settings-card">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Homepage settings</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Admin levers for the public counsellor card.</div>
                </div>
                {prof.is_featured
                    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider" data-testid="homepage-settings-featured-pill">Featured</span>
                    : (
                        <Button size="sm" variant="outline" className="rounded-full text-[11px] h-7" onClick={feature} disabled={featuring} data-testid="homepage-settings-feature-btn">
                            {featuring ? "…" : "Feature"}
                        </Button>
                    )
                }
            </div>

            {/* Admin-only editable */}
            <div className="grid grid-cols-2 gap-3">
                <Fld label="Rating (0-5)">
                    <Input type="number" min="0" max="5" step="0.1" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="homepage-rating" />
                </Fld>
                <Fld label="Ratings count">
                    <Input type="number" min="0" value={form.ratings_count} onChange={(e) => setForm({ ...form, ratings_count: e.target.value })} data-testid="homepage-ratings-count" />
                </Fld>
                <Fld label="Sort order">
                    <Input type="number" min="0" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="homepage-sort-order" />
                </Fld>
                <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.is_available_now}
                            onChange={(e) => setForm({ ...form, is_available_now: e.target.checked })}
                            className="h-4 w-4 accent-primary"
                            data-testid="homepage-avail-override"
                        />
                        <span>{form.is_available_now ? "Available" : "Offline"}</span>
                    </label>
                </div>
            </div>
            <div className="flex justify-end">
                <Button size="sm" onClick={save} disabled={saving} data-testid="homepage-settings-save">
                    {saving ? "Saving…" : "Save"}
                </Button>
            </div>

            {/* Read-only preview of PES-owned fields */}
            <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-2" data-testid="homepage-settings-preview">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Public profile · read-only</div>
                    <span className="text-[9px] text-muted-foreground italic">Edited by {firstName} in My Profile</span>
                </div>
                <ProfilePreviewRow label="Credentials" value={member.credentials} />
                <ProfilePreviewRow label="Designation" value={member.designation} />
                <ProfilePreviewRow label="Languages" value={languages} />
                <ProfilePreviewRow label="Public bio" value={member.public_bio} multiline />
            </div>
        </div>
    );
}


function ProfilePreviewRow({ label, value, multiline }) {
    const empty = !value;
    return (
        <div className="flex items-baseline gap-2">
            <span className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground w-20 shrink-0">{label}</span>
            <span
                className={`text-[11.5px] flex-1 min-w-0 ${empty ? "text-muted-foreground/60 italic" : "text-foreground"} ${multiline ? "line-clamp-2 whitespace-pre-line" : "truncate"}`}
                data-testid={`homepage-preview-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
                {empty ? "— not set —" : value}
            </span>
        </div>
    );
}

function Fld({ label, children }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
function Row({ label, children }) {
    return (
        <div className="flex items-baseline gap-3">
            <dt className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground w-24 shrink-0">{label}</dt>
            <dd className="text-sm flex-1 min-w-0 break-words">{children}</dd>
        </div>
    );
}

/* ============================ Permissions tab ============================ */
function PermissionsTab({ member, catalog, onChanged }) {
    const initialPerms = member.effective_permissions || {};
    const isSuperuser = initialPerms["*"]?.includes("*");
    const [perms, setPerms] = useState(initialPerms);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    React.useEffect(() => {
        setPerms(member.effective_permissions || {});
        setDirty(false);
    }, [member.id, member.has_permission_overrides]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!catalog) return <div className="text-muted-foreground text-sm">Loading…</div>;

    if (isSuperuser) {
        return (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5" data-testid="coe-profile-permissions">
                <Crown size={20} weight="fill" className="text-amber-600 inline mr-2" />
                <span className="font-semibold text-amber-900">Superuser.</span>
                <span className="text-amber-800"> This user has unrestricted access to every resource and action.</span>
            </div>
        );
    }

    const toggle = (resource, action) => {
        const current = new Set(perms[resource] || []);
        if (current.has(action)) current.delete(action); else current.add(action);
        const next = { ...perms };
        if (current.size === 0) delete next[resource];
        else next[resource] = Array.from(current);
        setPerms(next);
        setDirty(true);
    };

    const toggleRow = (resource) => {
        const isFull = (perms[resource] || []).length === catalog.actions.length;
        const next = { ...perms };
        if (isFull) delete next[resource];
        else next[resource] = [...catalog.actions];
        setPerms(next);
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/coe/people/${member.id}/permissions`, { permissions: perms });
            toast.success("Permissions saved");
            onChanged?.();
            setDirty(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setSaving(false);
    };

    const reset = async () => {
        if (!await confirmDialog({ title: `Reset permissions to ${member.role_default_label} defaults? This will discard the custom permission set on this user.`, confirmText: "Confirm", tone: "danger" })) return;
        setSaving(true);
        try {
            await api.delete(`/admin/coe/people/${member.id}/permissions`);
            toast.success("Reset to role defaults");
            onChanged?.();
            setDirty(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setSaving(false);
    };

    return (
        <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="coe-profile-permissions">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Effective permissions</div>
                    <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                        {member.has_permission_overrides ? (
                            <>
                                This user has <span className="font-semibold text-amber-700">per-user overrides</span> that take precedence over their{" "}
                                <span className="font-semibold">{member.role_default_label}</span> role. Toggle below to edit.
                            </>
                        ) : (
                            <>
                                Inherited from the <span className="font-semibold">{member.role_default_label}</span> role. Edit below to override for just this user.
                            </>
                        )}
                    </p>
                </div>
                <div className="flex gap-2 shrink-0">
                    {member.has_permission_overrides && (
                        <Button variant="outline" onClick={reset} disabled={saving}
                            className="rounded-full text-rose-600 border-rose-200" data-testid="coe-perms-reset">
                            Reset to role
                        </Button>
                    )}
                    <Button onClick={save} disabled={saving || !dirty} className="rounded-full" data-testid="coe-perms-save">
                        {saving ? "Saving…" : (member.has_permission_overrides ? "Save overrides" : "Save as override")}
                    </Button>
                </div>
            </div>
            <div className="rounded-xl border border-border overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/40">
                        <tr>
                            <th className="px-3 py-2 text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground text-left">Resource</th>
                            {catalog.actions.map((a) => (
                                <th key={a} className="px-2 py-2 text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground text-center w-16">{a}</th>
                            ))}
                            <th className="px-2 py-2 text-center w-12"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {catalog.resources.map((res) => {
                            const acts = new Set(perms[res.key] || []);
                            const allOn = acts.size === catalog.actions.length;
                            const grantedCount = acts.size;
                            return (
                                <tr key={res.key} className={`border-t border-border ${grantedCount === 0 ? "opacity-60" : ""}`}>
                                    <td className="px-3 py-2 align-top">
                                        <div className="font-mono uppercase text-[11px] tracking-wide font-bold">{res.key}</div>
                                        <div className="text-[11px] text-muted-foreground">{res.label}</div>
                                    </td>
                                    {catalog.actions.map((a) => (
                                        <td key={a} className="text-center">
                                            <input
                                                type="checkbox"
                                                checked={acts.has(a)}
                                                onChange={() => toggle(res.key, a)}
                                                className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                                                data-testid={`coe-profile-perm-${res.key}-${a}`}
                                                aria-label={`${res.key} ${a}`}
                                            />
                                        </td>
                                    ))}
                                    <td className="text-center">
                                        <button
                                            type="button"
                                            onClick={() => toggleRow(res.key)}
                                            className="text-[10px] uppercase tracking-wider font-bold text-primary hover:text-primary/80"
                                            data-testid={`coe-profile-perm-row-${res.key}`}
                                        >
                                            {allOn ? "Clear" : "All"}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ============================ Workload tab ============================ */
const KIND_PILL = {
    web: { label: "Web", cls: "bg-sky-100 text-sky-700 border-sky-200", Icon: Globe },
    b2b: { label: "B2B", cls: "bg-violet-100 text-violet-700 border-violet-200", Icon: Handshake },
    application: { label: "App", cls: "bg-amber-100 text-amber-700 border-amber-200", Icon: Briefcase },
};

function WorkloadTab({ memberId }) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get(`/admin/coe/people/${memberId}/leads`).then((r) => setRows(r.data || []));
    }, [memberId]);

    if (rows === null) return <div className="text-muted-foreground text-sm">Loading workload…</div>;
    if (rows.length === 0) {
        return (
            <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="coe-profile-workload-empty">
                <ChartBar size={28} className="mx-auto text-muted-foreground mb-2" />
                <div className="font-display text-lg font-bold">No leads owned yet.</div>
                <p className="text-xs text-muted-foreground mt-1">When this member is assigned a lead, it'll show up here.</p>
            </div>
        );
    }

    const open = rows.filter((r) => !r.is_closed);
    const closed = rows.filter((r) => r.is_closed);

    return (
        <div className="space-y-5" data-testid="coe-profile-workload">
            {open.length > 0 && (
                <Section title={`Open · ${open.length}`} rows={open} />
            )}
            {closed.length > 0 && (
                <Section title={`Recently closed · ${closed.length}`} rows={closed} muted />
            )}
        </div>
    );

    function Section({ title, rows, muted }) {
        return (
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                    {title}
                </div>
                {rows.map((r) => {
                    const kp = KIND_PILL[r.kind];
                    return (
                        <Link
                            key={`${r.kind}-${r.id}`}
                            to={`/admin/leads/${r.kind}/${r.id}`}
                            className={`flex items-center gap-3 px-5 py-3 border-b border-border last:border-0 hover:bg-secondary/30 transition ${muted ? "opacity-70" : ""}`}
                            data-testid={`coe-profile-lead-${r.kind}-${r.id}`}
                        >
                            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold inline-flex items-center gap-1 shrink-0 ${kp.cls}`}>
                                <kp.Icon size={10} weight="fill" /> {kp.label}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm truncate">{r.title}</div>
                                <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                                    <span className="font-mono uppercase">{(r.status || "").replace(/_/g, " ")}</span>
                                    {r.assigned_at && <span>Assigned {fmtDT(r.assigned_at)}</span>}
                                </div>
                            </div>
                            <ArrowRight size={13} className="text-muted-foreground shrink-0" />
                        </Link>
                    );
                })}
            </div>
        );
    }
}

/* ============================ Activity tab ============================ */
function ActivityTab({ memberId }) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get(`/admin/coe/people/${memberId}/activity`, { params: { limit: 150 } })
            .then((r) => setRows(r.data || []));
    }, [memberId]);

    if (rows === null) return <div className="text-muted-foreground text-sm">Loading activity…</div>;
    if (rows.length === 0) {
        return (
            <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="coe-profile-activity-empty">
                <ClockClockwise size={28} className="mx-auto text-muted-foreground mb-2" />
                <div className="font-display text-lg font-bold">No recorded activity yet.</div>
                <p className="text-xs text-muted-foreground mt-1">Every action they take across the system will appear here.</p>
            </div>
        );
    }
    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="coe-profile-activity">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-3">
                Actions performed ({rows.length})
            </div>
            <ul className="space-y-3 max-h-[700px] overflow-y-auto pr-1">
                {rows.map((e) => (
                    <li key={e.id} className="flex gap-3 pb-3 border-b border-border last:border-0" data-testid={`coe-profile-activity-row-${e.id}`}>
                        <div className="w-7 h-7 rounded-full bg-secondary grid place-items-center shrink-0">
                            <IdentificationCard size={11} weight="duotone" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                <div className="text-sm">
                                    <span className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">{e.action.replace(/_/g, " ")}</span>
                                    {e.entity_type && (
                                        <span className="ml-2 text-[10px] uppercase font-mono text-muted-foreground">on {e.entity_type}</span>
                                    )}
                                </div>
                                <div className="text-[10px] text-muted-foreground tabular-nums">{fmtDT(e.created_at)}</div>
                            </div>
                            {e.summary && <div className="text-xs text-muted-foreground mt-0.5">{e.summary}</div>}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* ============================ Audit tab ============================ */
function AuditTab({ memberId }) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get(`/admin/coe/people/${memberId}/audit`).then((r) => setRows(r.data || []));
    }, [memberId]);

    if (rows === null) return <div className="text-muted-foreground text-sm">Loading audit log…</div>;
    if (rows.length === 0) {
        return (
            <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="coe-profile-audit-empty">
                <ClockClockwise size={28} className="mx-auto text-muted-foreground mb-2" />
                <div className="font-display text-lg font-bold">No changes recorded.</div>
                <p className="text-xs text-muted-foreground mt-1">Edits, role changes, activations, and invite acceptance for this member will appear here.</p>
            </div>
        );
    }
    return (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="coe-profile-audit">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-3">
                Changes to this record ({rows.length})
            </div>
            <ul className="space-y-3 max-h-[700px] overflow-y-auto pr-1">
                {rows.map((e) => (
                    <li key={e.id} className="flex gap-3 pb-3 border-b border-border last:border-0" data-testid={`coe-profile-audit-row-${e.id}`}>
                        <div className="w-7 h-7 rounded-full bg-secondary grid place-items-center text-[10px] font-bold text-muted-foreground shrink-0">
                            {(e.actor_name || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                <div className="text-sm">
                                    <span className="font-semibold">{e.actor_name || "System"}</span>
                                    <span className="ml-2 text-[10px] uppercase tracking-wide font-bold text-muted-foreground">{e.action.replace(/_/g, " ")}</span>
                                </div>
                                <div className="text-[10px] text-muted-foreground tabular-nums">{fmtDT(e.created_at)}</div>
                            </div>
                            {e.summary && <div className="text-xs text-muted-foreground mt-0.5">{e.summary}</div>}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}


/* ==================== Planned cases tab (iter101.28) ====================
   Per-planner throughput view — total finalised, this month, this week,
   active in-flight, refinement rate. Below: the raw case history so admin
   can drill into any one. Only rendered for staff_type=planner (tab
   filtered out for other types). */
function PlannedCasesTab({ memberId }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/admin/coe/people/${memberId}/cases`).then((r) => setData(r.data));
    }, [memberId]);

    if (data === null) return <div className="text-muted-foreground text-sm">Loading planned cases…</div>;
    if (!data.is_planner) {
        return (
            <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="coe-profile-cases-empty">
                <Factory size={28} className="mx-auto text-muted-foreground mb-2" />
                <div className="font-display text-lg font-bold">Not a planner.</div>
                <p className="text-xs text-muted-foreground mt-1">Case throughput is tracked for planner members only.</p>
            </div>
        );
    }

    const k = data.kpis || {};
    const cases = data.cases || [];
    const finalised = cases.filter((c) => c.is_finalised);
    const active = cases.filter((c) => !c.is_finalised && !c.is_terminal);
    const archived = cases.filter((c) => c.is_terminal);

    return (
        <div className="space-y-6" data-testid="coe-profile-cases">
            {/* KPI STRIP */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <PlannedKpi testid="planned-kpi-total" Icon={ClipboardText} tone="primary" label="Total assigned" value={k.total ?? 0} hint="Every case ever routed" />
                <PlannedKpi testid="planned-kpi-finalised" Icon={CheckCircle} tone="emerald" label="Finalised · lifetime" value={k.finalised_total ?? 0} hint="Past MFG spec" />
                <PlannedKpi testid="planned-kpi-month" Icon={Factory} tone="primary" label="This month" value={k.finalised_this_month ?? 0} hint="Handed to production" />
                <PlannedKpi testid="planned-kpi-week" Icon={Factory} tone="violet" label="This week" value={k.finalised_this_week ?? 0} hint="Rolling 7-day" />
                <PlannedKpi testid="planned-kpi-active" Icon={Clock} tone="amber" label="Active open" value={k.active_open ?? 0} hint="Still in your queue" />
                <PlannedKpi testid="planned-kpi-refinement" Icon={ArrowsCounterClockwise} tone="rose" label="Refinement rate" value={`${k.refinement_rate_pct ?? 0}%`} hint={`${k.refinement_count ?? 0} of ${k.total ?? 0} cases`} />
            </div>

            {/* CASE SECTIONS */}
            {active.length > 0 && <PlannedCaseSection title={`Active · ${active.length}`} rows={active} />}
            {finalised.length > 0 && <PlannedCaseSection title={`Finalised · ${finalised.length}`} rows={finalised} />}
            {archived.length > 0 && <PlannedCaseSection title={`Terminal · ${archived.length}`} rows={archived} muted />}
            {cases.length === 0 && (
                <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="coe-profile-cases-list-empty">
                    <Factory size={28} className="mx-auto text-muted-foreground mb-2" />
                    <div className="font-display text-lg font-bold">No cases yet.</div>
                    <p className="text-xs text-muted-foreground mt-1">Once Admin routes cases to this planner they&apos;ll appear here.</p>
                </div>
            )}
        </div>
    );
}

function PlannedKpi({ testid, Icon, tone, label, value, hint }) {
    const accent = tone === "emerald" ? "text-emerald-700 bg-emerald-100"
        : tone === "violet" ? "text-violet-700 bg-violet-100"
        : tone === "amber" ? "text-amber-700 bg-amber-100"
        : tone === "rose" ? "text-rose-700 bg-rose-100"
        : "text-primary bg-primary/10";
    return (
        <div
            className="flex items-start gap-3 rounded-xl bg-card border border-border p-3"
            data-testid={testid}
        >
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1">{value}</div>
                {hint && <div className="text-[10px] text-muted-foreground mt-1 truncate">{hint}</div>}
            </div>
        </div>
    );
}

function PlannedCaseSection({ title, rows, muted }) {
    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                {title}
            </div>
            {rows.map((r) => (
                <Link
                    key={r.id}
                    to={`/admin/cases/${r.id}`}
                    className={`grid grid-cols-12 gap-3 items-center px-5 py-3 border-b border-border last:border-0 hover:bg-secondary/30 transition ${muted ? "opacity-70" : ""}`}
                    data-testid={`coe-profile-case-${r.id}`}
                >
                    <div className="col-span-3 min-w-0">
                        <div className="font-mono text-xs font-bold text-primary truncate">{r.code || r.id.slice(0, 8)}</div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                            {r.planning_type ? (r.planning_type === "inhouse" ? "In-house" : "Outsourced") : "—"}
                            {r.case_kind === "refinement" && <span className="ml-1 text-amber-700 font-semibold">· Refinement</span>}
                            {r.case_kind === "retainer" && <span className="ml-1 text-violet-700 font-semibold">· Retainer</span>}
                        </div>
                    </div>
                    <div className="col-span-3 min-w-0">
                        <div className="text-sm font-semibold truncate">{r.patient_name || "—"}</div>
                    </div>
                    <div className="col-span-3 min-w-0">
                        <div className="text-xs truncate">{r.doctor_name || "—"}</div>
                    </div>
                    <div className="col-span-2 text-[11px] text-muted-foreground truncate">
                        {(r.status || "").replace(/_/g, " ")}
                    </div>
                    <div className="col-span-1 text-right text-[10px] text-muted-foreground tabular-nums">
                        {fmtDT(r.status_updated_at || r.created_at).split(",")[0]}
                    </div>
                </Link>
            ))}
        </div>
    );
}

